/**
 * Groups Query Keys Factory
 * Centralized query key generation for groups-related queries
 * Used by both query stores and middleware
 */

import type { GroupMemberRole } from '../types/types'

/**
 * Creates query key for group by slug
 * @param groupSlug - Group slug identifier
 * @returns Query key array
 */
export function createGroupBySlugQueryKey(groupSlug: string): readonly unknown[] {
  return ['groups', 'slug', groupSlug] as const
}

/**
 * Creates query key for group by ID
 * @param groupId - Group ID
 * @returns Query key array
 */
export function createGroupByIdQueryKey(groupId: string): readonly unknown[] {
  return ['groups', 'id', groupId] as const
}

/**
 * Creates query key for groups list (used by infinite query and cache lookups).
 * Keyed only by search – limit/skip are fetch options (pageParam handles pagination), not cache identity.
 * Call with no args (or empty search) for prefix / default list: returns ['groups', 'list'] for findAll(exact: false).
 */
export function createGroupsListQueryKey(params?: { search?: string }): readonly unknown[] {
  const search = params?.search?.trim()
  if (!search) return ['groups', 'list'] as const
  return ['groups', 'list', search] as const
}

/**
 * Groups query keys factory
 */
export const groupsQueryKeys = {
  all: ['groups'] as const,
  groups: createGroupsListQueryKey,
  groupBySlug: createGroupBySlugQueryKey,
  groupById: createGroupByIdQueryKey,
  pinnedGroups: () => ['groups', 'pinned'] as const,
  groupMembers: (groupId: string, params?: { limit?: number; skip?: number; search?: string }) =>
    ['groups', groupId, 'members', params] as const,
  groupMembersDetails: (
    groupId: string,
    params?: {
      limit?: number
      skip?: number
      search?: string
      memberRole?: string
      memberStatus?: string
    }
  ) => ['groups', groupId, 'members-details', params] as const,
  groupMembersList: (groupId: string, locationId: string, filters: GroupMemberRole[]) =>
    ['groups', groupId, 'members-list', locationId, filters] as const,
  membershipForm: (groupId: string | undefined, formId: string, locationId?: string) =>
    ['groups', groupId, locationId, 'membership-form', formId] as const,
  membershipCustomFields: (groupId: string, locationId: string) => ['groups', groupId, locationId, 'membership-custom-fields'] as const,
  userGroups: (locationId?: string) => ['groups', 'user', locationId] as const,
  discovery: (locationId?: string, params?: { limit?: number }) => ['groups', 'discovery', locationId, params] as const,
  /** Partial key: all discovery queries regardless of location / params */
  discoveryAll: () => ['groups', 'discovery'] as const,
  /** Partial key: all groups-* queries scoped to one group id (members, slug cache slices, etc.) */
  groupScoped: (groupId: string) => ['groups', groupId] as const,
} as const
